import { Fragment, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { useIntl } from 'dibs-react-intl';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement.ts';
import slugify from 'dibs-slugify/exports/slugify.ts';
import { getPdpDimensionSegments } from '../helpers/formatPdpDimensionValues.ts';

import {
    PdpSharedDetail,
    type PdpSharedDetailProps,
} from '../shared/PdpSharedDetail/PdpSharedDetail.tsx';
import { PdpSharedDetailRingSize } from '../shared/PdpSharedDetail/PdpSharedDetailRingSize.tsx';
import { PdpSharedDetailCondition } from '../PdpSharedDetails/PdpSharedDetailCondition.tsx';
import { PdpSharedDetailMovement } from '../shared/PdpSharedDetail/PdpSharedDetailMovement.tsx';
import { PdpSharedDetailFramingOptions } from '../PdpSharedDetails/PdpSharedDetailFramingOptions.tsx';
import { PdpSharedDetailReference } from '../shared/PdpSharedDetail/PdpSharedDetailReference.tsx';
import { PdpSharedDetailLabReport } from '../PdpSharedDetails/PdpSharedDetailLabReport.tsx';
import { PdpDimensionUnitToggle } from './PdpDimensionUnitToggle.tsx';
import { usePdpDimensionUnit } from '../hooks/usePdpDimensionUnit.ts';
import { PdpRespAboutItemPanelItemModuleDetail } from './PdpRespAboutItemPanelItemModuleDetail.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useCurrency } from '../../shared/hooks/useCurrency.ts';
import { isCreatorDetailKey } from '../helpers/isCreatorDetailKey.ts';

import { type PdpRespAboutItemPanelItemModuleDetails_item$key } from './__generated__/PdpRespAboutItemPanelItemModuleDetails_item.graphql.ts';
import { type PdpRespAboutItemPanelItemModuleDetails_item$data } from './__generated__/PdpRespAboutItemPanelItemModuleDetails_item.graphql.ts';
import { type PdpRespAboutItemPanelItemModuleDetails_user$key } from './__generated__/PdpRespAboutItemPanelItemModuleDetails_user.graphql.ts';

const detailsToIgnore = ['FRAMING_STATUS'];
const DIMENSION_DETAIL_KEYS = ['DIMENSIONS', 'CASE_DIMENSIONS'];

type Item = NonNullable<PdpRespAboutItemPanelItemModuleDetails_item$data>;
type PdpDetailsDisplay = NonNullable<ArrayElement<Item['pdpDetailsDisplay']>>;

type RenderDetailsProps = {
    detailKey: PdpDetailsDisplay['detailKey'];
    delimiter: PdpDetailsDisplay['delimiter'];
    detailValues: PdpDetailsDisplay['detailValues'];
};

const notDisplayableDetails = (
    detail: PdpDetailsDisplay,
    shouldExcludeCreatorDetails: boolean
): boolean =>
    !detailsToIgnore.includes(detail?.detailKey || '') &&
    !(shouldExcludeCreatorDetails && isCreatorDetailKey(detail?.detailKey));

const itemFragment = graphql`
    fragment PdpRespAboutItemPanelItemModuleDetails_item on Item {
        serviceId
        pdpDetailsDisplay {
            detailKey
            delimiter
            detailDisplayName
            detailValues {
                __typename
                # eslint-disable-next-line relay/unused-fields - used by formatPdpDimensionValues
                displayName
                ...PdpSharedDetail_detailValues
                ...PdpSharedDetailRingSize_detailValues
                ...PdpSharedDetailMovement_detailValues
                ... on PdpDetailDimensionsValue {
                    detailKey
                    # eslint-disable-next-line relay/unused-fields - used by formatPdpDimensionValues
                    measurementDisplayName
                    metricValue
                    inchesValue
                }
            }
        }
        ...PdpSharedDetailFramingOptions_item
        ...PdpSharedDetailReference_item
    }
`;

const userFragment = graphql`
    fragment PdpRespAboutItemPanelItemModuleDetails_user on User {
        serviceId
        preferences {
            measurementUnit
        }
        ...PdpSharedDetailFramingOptions_user
        ...PdpSharedDetailReference_user
    }
`;

type Props = {
    item: PdpRespAboutItemPanelItemModuleDetails_item$key;
    user: PdpRespAboutItemPanelItemModuleDetails_user$key | null | undefined;
    afterDisplay: boolean;
    selectedSkuId?: string | null;
    excludeCreatorDetails?: boolean;
};

export const PdpRespAboutItemPanelItemModuleDetails: FC<Props> = ({
    item: itemRef,
    user: userRef,
    afterDisplay,
    selectedSkuId,
    excludeCreatorDetails = false,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { formatMessage } = useIntl();
    const { isDesktop } = useServerVarsContext();
    const { currency } = useCurrency();

    const [measurementUnit, setMeasurementUnit] = usePdpDimensionUnit({
        userMeasurementUnit: user?.preferences?.measurementUnit,
        userServiceId: user?.serviceId,
    });

    const details = item?.pdpDetailsDisplay || [];

    const RenderDetails: FC<RenderDetailsProps> = ({ detailKey, detailValues, delimiter }) => {
        const sharedProps: PdpSharedDetailProps = {
            currency,
            // @ts-ignore parent fragment data is a valid spread key for these detail components
            detailValues,
            selectedSkuId,
            delimiter,
            displayValuesAs: 'inline-block',
        };

        if (detailKey === 'REFERENCE_NUMBER') {
            return (
                <PdpSharedDetailReference
                    {...sharedProps}
                    displayValuesAs="block"
                    item={item}
                    user={user}
                />
            );
        }

        if (detailKey === 'FRAMING') {
            const frameIncludedDetails = details?.find(
                detail => detail?.detailKey === 'FRAMING_STATUS'
            );
            return (
                <>
                    {!!frameIncludedDetails && <div>{frameIncludedDetails.detailDisplayName}</div>}
                    <PdpSharedDetailFramingOptions {...sharedProps} item={item} user={user} />
                </>
            );
        }

        if (detailKey === 'CONDITION' && item?.serviceId) {
            return <PdpSharedDetailCondition {...sharedProps} itemId={item.serviceId} />;
        }

        if (detailKey === 'MOVEMENT') {
            return <PdpSharedDetailMovement {...sharedProps} />;
        }

        if (detailKey === 'RING_SIZE') {
            // @ts-ignore Ring Size uses a different __typename than other detail values
            return <PdpSharedDetailRingSize detailValues={detailValues} currency={currency} />;
        }

        if (detailKey === 'LAB_REPORT') {
            return (
                <PdpSharedDetailLabReport {...sharedProps} afterDisplay={afterDisplay} showOnTop />
            );
        }

        if (DIMENSION_DETAIL_KEYS.includes(detailKey || '')) {
            const dimensionSegments = getPdpDimensionSegments(
                detailValues,
                measurementUnit,
                formatMessage
            );

            if (dimensionSegments.length > 0) {
                return (
                    <>
                        {dimensionSegments.map((segment, segmentIndex) => (
                            <Fragment key={segmentIndex}>
                                {segmentIndex > 0 ? ', ' : null}
                                <span className={dibsCss.whitespaceNowrap}>{segment}</span>
                            </Fragment>
                        ))}
                    </>
                );
            }
        }

        return <PdpSharedDetail {...sharedProps} />;
    };

    const detailMap = details
        .filter(filterNulls)
        .filter(detail => notDisplayableDetails(detail, excludeCreatorDetails))
        .map((detail, index) => {
            const { detailKey, detailDisplayName, delimiter, detailValues } = detail;
            const showUnitToggle =
                DIMENSION_DETAIL_KEYS.includes(detailKey || '') &&
                (detailValues || []).some(value => value?.inchesValue || value?.metricValue);

            const dimensionTitle = showUnitToggle ? (
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.gapXxsmall,
                        isDesktop
                            ? [dibsCss.flexWrap, dibsCss.itemsCenter]
                            : [dibsCss.flexCol, dibsCss.itemsStart]
                    )}
                >
                    {detailDisplayName}
                    <PdpDimensionUnitToggle value={measurementUnit} onChange={setMeasurementUnit} />
                </div>
            ) : (
                detailDisplayName
            );

            return (
                <PdpRespAboutItemPanelItemModuleDetail
                    title={dimensionTitle}
                    dataTn={slugify(detailDisplayName)}
                    key={`${detailKey}-${index}`}
                >
                    <RenderDetails
                        detailKey={detailKey}
                        detailValues={detailValues}
                        delimiter={delimiter}
                    />
                </PdpRespAboutItemPanelItemModuleDetail>
            );
        });

    const midpoint = Math.ceil(detailMap.length / 2);
    const leftDetails = detailMap.slice(0, midpoint);
    const rightDetails = detailMap.slice(midpoint);

    const columnClassName = classnames(
        dibsCss.flex,
        dibsCss.flexCol,
        dibsCss.gapXsmall,
        dibsCss.flex1,
        dibsCss.minW0
    );

    return (
        <div className={dibsCss.minW0} data-tn="pdp-details">
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.wFull,
                    dibsCss.minW0,
                    isDesktop ? dibsCss.flexRow : dibsCss.flexCol,
                    isDesktop ? dibsCss.gapLarge : dibsCss.gapXsmall
                )}
            >
                <div className={columnClassName}>{leftDetails}</div>
                <div className={columnClassName}>{rightDetails}</div>
            </div>
        </div>
    );
};
